// Centralised brand & content data for Reflex Stone Care
import {
  Sparkles, Hammer, Shield, Droplets, Wrench, Building2,
  Gem, Brush, Layers, ScissorsLineDashed, Ruler, Grid3x3,
} from "lucide-react";

export const BRAND = {
  name: "Reflex Stone Care",
  tagline: "Sydney's premium stone restoration specialists",
  contactPerson: "Sacha Savini",
  phone: "+61 450 421 964",
  phoneHref: "tel:+61450421964",
  email: "Sacha@reflexstonecare.com.au",
  emailHref: "mailto:Sacha@reflexstonecare.com.au",
  serviceArea: "Sydney & Central Coast",
  experienceYears: 50,
  hours: "Mon – Sat · 7:00 – 18:00",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Stone Types", to: "/stone-types" },
  { label: "Commercial", to: "/commercial" },
  { label: "Gallery", to: "/gallery" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

// ---------- Real Reflex Stone Care project photos (16 images) ----------
export const IMG = {
  grandFoyer: "/images/grand-foyer.jpg",                 // checkered marble + chandelier (HERO)
  harborView: "/images/harbor-view-floor.jpg",           // light marble floor with Sydney harbor
  blackStaircase: "/images/black-marble-staircase.jpg",  // black marble + brass stair
  commercialLobby: "/images/commercial-marble-lobby.jpg",// geometric multi-stone lobby
  mosaicTerrazzo: "/images/mosaic-terrazzo.jpg",         // mosaic terrazzo art floor
  quartziteIsland: "/images/quartzite-island.jpg",       // kitchen island benchtop
  greyMarbleBathroom: "/images/grey-marble-bathroom.jpg",// grey marble bath w/ skylight
  marbleLounge: "/images/marble-lounge.jpg",             // polished marble lounge
  marbleBathroom: "/images/marble-bathroom.jpg",         // dark marble bathroom floor
  travertineKitchen: "/images/travertine-kitchen.jpg",   // travertine kitchen w/ view
  travertineBathroom: "/images/travertine-bathroom.jpg", // travertine ensuite
  sandstoneExterior: "/images/sandstone-exterior.jpg",   // sandstone home corner
  sandstoneCourtyard: "/images/sandstone-courtyard.jpg", // sandstone courtyard mid-clean
  flagstoneDriveway: "/images/flagstone-driveway.jpg",   // flagstone driveway pattern
  cleaningProcess: "/images/cleaning-process.jpg",       // tile cleaning in progress
  tilingInstall: "/images/tiling-install.jpg",           // tiler installing marble-look tile
};

export const SERVICES = [
  {
    slug: "marble-restoration",
    title: "Marble Restoration",
    icon: Gem,
    short: "Re-cut, hone and polish marble back to factory mirror finish.",
    long:
      "Diamond grinding removes etch marks, lippage and decades of wear. We re-finish to your chosen sheen — matte, satin or full mirror polish — sealed for everyday luxury.",
    image: IMG.grandFoyer,
  },
  {
    slug: "terrazzo-restoration",
    title: "Terrazzo Restoration",
    icon: Layers,
    short: "Heritage and modern terrazzo refinished to a glass-like polish.",
    long:
      "Aggressive flattening, full re-grout where required, fine honing and high-speed polishing. Perfect for hotel lobbies, apartment foyers and heritage homes.",
    image: IMG.mosaicTerrazzo,
  },
  {
    slug: "limestone-restoration",
    title: "Limestone Restoration",
    icon: Brush,
    short: "Honed limestone & travertine, rid of stains and traffic wear.",
    long:
      "Specialty pads and pH-neutral systems lift staining without dulling the stone. Re-honed and impregnated with premium sealers for a soft, natural finish.",
    image: IMG.travertineBathroom,
  },
  {
    slug: "stone-polishing",
    title: "Stone Polishing",
    icon: Sparkles,
    short: "Mirror, satin or matte finishes on any natural stone surface.",
    long:
      "Granite, marble, basalt, bluestone and engineered stone — polished using calibrated diamond resin pads for an even, defect-free reflection.",
    image: IMG.blackStaircase,
  },
  {
    slug: "stone-sealing",
    title: "Stone Sealing",
    icon: Shield,
    short: "Premium impregnators that protect for up to 15 years.",
    long:
      "Penetrating fluoropolymer and siloxane sealers repel oil and water without changing the colour of the stone. Tested and certified product range only.",
    image: IMG.flagstoneDriveway,
  },
  {
    slug: "tile-grout-cleaning",
    title: "Tile & Grout Cleaning",
    icon: Droplets,
    short: "Deep clean, re-colour and seal grout lines to look new.",
    long:
      "Hot-water extraction with alkaline pre-treatment lifts decades of soiling. Optional grout colour seal restores uniformity and hygiene in wet areas.",
    image: IMG.sandstoneCourtyard,
  },
  {
    slug: "tiling",
    title: "Tiling",
    icon: Grid3x3,
    short: "Precision tiling with natural stone, porcelain and ceramic.",
    long:
      "Full tiling service — layout planning, substrate preparation, waterproofing and installation of natural stone, porcelain and ceramic across floors, walls, bathrooms and outdoor areas. Straight, level, perfectly aligned grout lines. Finished with the right sealer for the material and the room.",
    image: IMG.tilingInstall,
  },
  {
    slug: "scratch-chip-repairs",
    title: "Scratch & Chip Repairs",
    icon: ScissorsLineDashed,
    short: "Invisible repairs for chips, etch marks and deep scratches.",
    long:
      "Stone-matched epoxies, dustless grinders and feathered polishing — most repairs are undetectable to the eye and to the hand.",
    image: IMG.quartziteIsland,
  },
  {
    slug: "commercial-maintenance",
    title: "Commercial Maintenance",
    icon: Building2,
    short: "Scheduled programs for hotels, retail and corporate towers.",
    long:
      "Out-of-hours teams, KPI-driven service plans and full insurance. We protect the asset value of your stone for the long term.",
    image: IMG.commercialLobby,
  },
];

export const STONE_TYPES = [
  {
    name: "Marble",
    blurb: "Carrara, Calacatta, Statuario, Crema Marfil and more — refinished to original glory.",
    image: IMG.marbleLounge,
  },
  {
    name: "Polished Marble Floors",
    blurb: "Mirror-finish floors brought back to the day they were laid.",
    image: IMG.harborView,
  },
  {
    name: "Limestone",
    blurb: "Soft, porous and warm — restored without bleaching the natural patina.",
    image: IMG.travertineBathroom,
  },
  {
    name: "Travertine",
    blurb: "Filled, honed or polished — pitting expertly addressed and resealed.",
    image: IMG.travertineKitchen,
  },
  {
    name: "Granite & Quartzite",
    blurb: "Mirror polish, leathered or honed finishes — restored with diamond systems.",
    image: IMG.quartziteIsland,
  },
  {
    name: "Bluestone & Basalt",
    blurb: "Deep cleaning, slip-resistant honing and oil-stain extraction.",
    image: IMG.flagstoneDriveway,
  },
  {
    name: "Sandstone",
    blurb: "Sydney heritage steps, balconies and façades — gently restored and sealed.",
    image: IMG.sandstoneExterior,
  },
  {
    name: "Engineered Stone",
    blurb: "Caesarstone, Quantum Quartz, Smartstone — chip repairs and refinishing.",
    image: IMG.greyMarbleBathroom,
  },
];

export const WHY_US = [
  { icon: Hammer, title: "50+ years combined experience", body: "Two generations of craftsmen, schooled in European stone trades." },
  { icon: Wrench, title: "Advanced dust-free systems", body: "Sealed-vacuum machines mean we restore occupied homes without disruption." },
  { icon: Shield, title: "Fully insured · $20M public liability", body: "Documented site management and risk assessments on every job." },
  { icon: Sparkles, title: "Premium sealers & equipment", body: "Calibrated diamond systems and certified impregnators only." },
  { icon: Ruler, title: "Free on-site inspection", body: "Honest, written quotes after physically assessing the stone." },
  { icon: Building2, title: "Residential & commercial", body: "From luxury apartments to five-star hotel lobbies." },
];

export const STATS = [
  { value: 50, suffix: "+", label: "Years combined experience" },
  { value: 4200, suffix: "+", label: "Sydney projects completed" },
  { value: 320, suffix: "k", label: "m² of stone restored" },
  { value: 5.0, decimals: 1, label: "Average client rating" },
];

export const TESTIMONIALS = [
  {
    quote:
      "The marble in our Point Piper apartment looked tired and etched after years of entertaining. The team brought it back beyond what we thought possible. The finish is genuinely spectacular.",
    name: "Eleanor M.",
    role: "Homeowner · Point Piper",
  },
  {
    quote:
      "We engaged Reflex for a full lobby refurbishment. They worked overnight, kept the site immaculate and delivered a finish our chairman compares favourably to the day the building opened.",
    name: "James R.",
    role: "Hotel General Manager · CBD",
  },
  {
    quote:
      "Honest, punctual and obsessive about detail. Our terrazzo staircase looks like new poured stone. I now recommend them to every interior client.",
    name: "Sophia D.",
    role: "Interior Designer · Mosman",
  },
  {
    quote:
      "We've used three other contractors over the years. Reflex is the first that actually finished on the brief, on time and on budget. Quality is on another level.",
    name: "Michael T.",
    role: "Builder · Bondi",
  },
  {
    quote:
      "Pre-settlement defect on a Calacatta benchtop — fixed in two hours, completely invisible. They saved our handover.",
    name: "Priya S.",
    role: "Project Manager · Barangaroo",
  },
];

export const SERVICE_AREAS = [
  "Stone Restoration Sydney",
  "Marble Polishing Sydney",
  "Terrazzo Restoration Sydney",
  "Stone Cleaning Sydney",
  "Marble Restoration North Shore",
  "Stone Restoration Eastern Suburbs",
  "Stone Restoration Inner West",
  "Central Coast Stone Restoration",
  "Limestone Polishing Sydney",
  "Travertine Restoration Sydney",
  "Stone Sealing Sydney",
  "Granite Polishing Sydney",
];

export const FAQ = [
  { q: "How long does a typical marble polish take?", a: "Most residential floors are completed in one to two days. We grind, hone, polish and seal — leaving the stone fully cured and ready for foot traffic the next morning." },
  { q: "Will the work create dust in my home?", a: "Our diamond systems are sealed to industrial vacuums, capturing 99% of airborne particles. We also use containment sheeting on all openings so adjoining rooms stay clean." },
  { q: "Can you remove deep etch marks and water rings?", a: "Yes. Etching is microscopic acid damage that requires re-cutting the surface. We routinely remove etch marks that cleaners and DIY polishes cannot touch." },
  { q: "Do you service strata buildings and hotels?", a: "Daily. We carry $20M public liability, an SWMS for every site and run out-of-hours crews to minimise guest and resident disruption." },
  { q: "How much does stone restoration cost?", a: "Pricing depends on stone type, size and current condition. We provide a free, written quote after a short on-site inspection — no obligation." },
  { q: "What sealer do you use and how long does it last?", a: "Premium fluoropolymer and siloxane impregnators rated up to 15 years on benchtops and 10 years on floors. Receipts and product data sheets are included with every job." },
];

// Featured projects (Home page bento-grid). Use the most cinematic 5.
export const PROJECTS = [
  { title: "Harbour View Marble Living", location: "Vaucluse · Sydney", stone: "Marble", image: IMG.harborView },
  { title: "Black Marquina Staircase", location: "Bellevue Hill", stone: "Marble", image: IMG.blackStaircase },
  { title: "Grand Marble Foyer", location: "North Shore", stone: "Marble", image: IMG.grandFoyer },
  { title: "Mosaic Terrazzo Walkway", location: "CBD", stone: "Terrazzo", image: IMG.mosaicTerrazzo },
  { title: "Hotel Lobby Refurbishment", location: "Sydney CBD", stone: "Multi-Stone", image: IMG.commercialLobby },
];

// Full gallery — every photo we have, with descriptive captions.
export const GALLERY = [
  { title: "Grand Marble Foyer", location: "North Shore", stone: "Marble", image: IMG.grandFoyer },
  { title: "Harbour View Marble Living", location: "Vaucluse", stone: "Marble", image: IMG.harborView },
  { title: "Black Marquina Staircase", location: "Bellevue Hill", stone: "Marble", image: IMG.blackStaircase },
  { title: "Hotel Lobby Refurbishment", location: "Sydney CBD", stone: "Multi-Stone", image: IMG.commercialLobby },
  { title: "Mosaic Terrazzo Walkway", location: "CBD", stone: "Terrazzo", image: IMG.mosaicTerrazzo },
  { title: "Quartzite Kitchen Island", location: "Eastern Suburbs", stone: "Quartzite", image: IMG.quartziteIsland },
  { title: "Grey Marble Ensuite", location: "Mosman", stone: "Marble", image: IMG.greyMarbleBathroom },
  { title: "Polished Marble Lounge", location: "Eastern Suburbs", stone: "Marble", image: IMG.marbleLounge },
  { title: "Marble Bathroom Floor", location: "North Shore", stone: "Marble", image: IMG.marbleBathroom },
  { title: "Travertine Kitchen & Terrace", location: "Central Coast", stone: "Travertine", image: IMG.travertineKitchen },
  { title: "Travertine Ensuite", location: "Inner West", stone: "Travertine", image: IMG.travertineBathroom },
  { title: "Sandstone Heritage Façade", location: "North Shore", stone: "Sandstone", image: IMG.sandstoneExterior },
  { title: "Sandstone Courtyard Restoration", location: "Eastern Suburbs", stone: "Sandstone", image: IMG.sandstoneCourtyard },
  { title: "Flagstone Driveway Pattern", location: "Mosman", stone: "Bluestone & Sandstone", image: IMG.flagstoneDriveway },
  { title: "Tile & Grout Restoration", location: "Sydney", stone: "Process", image: IMG.cleaningProcess },
];

export const PROCESS_STEPS = [
  { n: "01", title: "Free on-site inspection", body: "We physically assess your stone, agree on the desired finish and provide a written, fixed-price quote." },
  { n: "02", title: "Site preparation", body: "Furniture protected, floors masked, dust containment installed. Your home or business stays clean." },
  { n: "03", title: "Diamond restoration", body: "Calibrated grinding, honing and polishing pads progressively bring the stone back to a flawless finish." },
  { n: "04", title: "Sealing & handover", body: "Premium impregnator applied, final inspection walked with you, aftercare guide left on completion." },
];
